import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { X, SquarePen, Mail, MessageCircle, User, Users, Star, Search, Bell } from "lucide-react";
import { useSchoolNewsDot, useSchoolNewsEnabled } from "@/hooks/useSchoolNewsEnabled";
import { CoachIcon } from "@/components/CoachIcon";
import { useServerNextStep } from "@/hooks/useNextStep";
import { toRelativeCtaPath } from "@/services/nextStep";
import { getStepDisplay } from "@/types/tips";
import BrandLogo from "@/assets/golfrecruit-lockup-light.svg";
import { useBranding } from "@/hooks/useBranding";
import { useEndUser } from "@/contexts/EndUserContext";
import { trackChatThreadResumed } from "@/analytics/analytics";
import { SITE_URL } from "@/constants/domains";

interface ChatHistoryItem {
  id: string;
  title: string;
  timestamp: string;
  messageCount?: number;
}

interface SidebarProps {
  chatHistory: ChatHistoryItem[];
  onNewChat: () => void;
  onClose?: () => void;
  onSelectChat?: (id: string) => void;
  activeId?: string | null;
  disableNewChat?: boolean;
  playerName?: string;
  classYear?: string;
  jgsRank?: string;
  scoringDifferential?: string;
  isLinked?: boolean;
  userType?: "player" | "coach";
}

const Sidebar = ({
  chatHistory,
  onNewChat,
  onClose,
  onSelectChat,
  activeId,
  disableNewChat,
  userType: userTypeProp,
}: SidebarProps) => {
  const schoolNewsEnabled = useSchoolNewsEnabled();
  const schoolNewsDot = useSchoolNewsDot();
  const {
    userType: contextUserType,
    dismissedNextSteps,
    dismissNextStep,
    resetDismissedNextSteps,
  } = useEndUser();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const hasAcademyBrand = Boolean(branding?.headerLogoUrl);
  const userType = userTypeProp ?? contextUserType;
  const location = useLocation();
  const isChatPage = location.pathname === "/chat";

  // Server-decided next best action (players only). Null while loading or
  // when the fetch fails — the card simply hides in those cases.
  const serverStep = useServerNextStep(userType === "player");
  const dismissed = dismissedNextSteps ?? [];

  const nextStep = useMemo(() => {
    if (userType !== "player" || !serverStep) return null;
    if (dismissed.includes(serverStep.stepKey)) return null;
    const display = getStepDisplay(serverStep.stepKey, serverStep.ctaLabel);
    return {
      key: serverStep.stepKey,
      label: display.title,
      description: display.description,
      to: toRelativeCtaPath(serverStep.ctaUrl),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, serverStep, dismissed.join(",")]);

  return (
    <aside className="w-80 bg-sidebar-background border-r border-border/40 flex flex-col h-full relative">
      {/* ── Mobile close ── */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden absolute right-2 top-2 h-8 w-8"
        onClick={onClose}
        aria-label="Close sidebar"
      >
        <X className="h-4 w-4" />
      </Button>

      {/* ── Primary navigation ── */}
      <nav className="shrink-0 px-3 pt-4 pb-3 flex flex-col gap-0.5">
        <NavLink
          to="/pipeline"
          onClick={onClose}
          className={({ isActive }) =>
            "h-9 rounded-lg px-3 flex items-center gap-3 text-sm font-medium transition-colors " +
            (isActive
              ? "bg-primary/10 text-primary-text"
              : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground")
          }
        >
          <Star className="h-4 w-4 shrink-0" />
          My Schools
        </NavLink>
        <NavLink
          to="/chat"
          onClick={onClose}
          className={({ isActive }) =>
            "h-9 rounded-lg px-3 flex items-center gap-3 text-sm font-medium transition-colors " +
            (isActive
              ? "bg-primary/10 text-primary-text"
              : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground")
          }
        >
          <MessageCircle className="h-4 w-4 shrink-0" />
          Ask Coach
        </NavLink>
        <NavLink
          to="/schools"
          onClick={onClose}
          className={({ isActive }) =>
            "h-9 rounded-lg px-3 flex items-center gap-3 text-sm font-medium transition-colors " +
            (isActive
              ? "bg-primary/10 text-primary-text"
              : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground")
          }
        >
          <Search className="h-4 w-4 shrink-0" />
          School Search
        </NavLink>
        {schoolNewsEnabled && (
          <NavLink
            to="/notifications"
            onClick={onClose}
            className={({ isActive }) =>
              "h-9 rounded-lg px-3 flex items-center gap-3 text-sm font-medium transition-colors " +
              (isActive
                ? "bg-primary/10 text-primary-text"
                : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground")
            }
          >
            <Bell className="h-4 w-4 shrink-0" />
            Notifications
            {schoolNewsDot && (
              <>
                <span
                  data-testid="notifications-dot"
                  aria-hidden="true"
                  className="ml-auto h-2 w-2 rounded-full bg-primary"
                />
                <span className="sr-only">new updates</span>
              </>
            )}
          </NavLink>
        )}
        <NavLink
          to="/outreach"
          onClick={onClose}
          className={({ isActive }) =>
            "h-9 rounded-lg px-3 flex items-center gap-3 text-sm font-medium transition-colors " +
            (isActive
              ? "bg-primary/10 text-primary-text"
              : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground")
          }
        >
          <Mail className="h-4 w-4 shrink-0" />
          Coach Emails
        </NavLink>
        <NavLink
          to="/profile"
          onClick={onClose}
          className={({ isActive }) =>
            "h-9 rounded-lg px-3 flex items-center gap-3 text-sm font-medium transition-colors " +
            (isActive
              ? "bg-primary/10 text-primary-text"
              : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground")
          }
        >
          <User className="h-4 w-4 shrink-0" />
          Profile
        </NavLink>
        {userType === "coach" && (
          <NavLink
            to="/coach"
            onClick={onClose}
            className={({ isActive }) =>
              "h-9 rounded-lg px-3 flex items-center gap-3 text-sm font-medium transition-colors " +
              (isActive
                ? "bg-primary/10 text-primary-text"
                : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground")
            }
          >
            <Users className="h-4 w-4 shrink-0" />
            Coach's Corner
          </NavLink>
        )}
      </nav>

      {/* ─── Next step card (players only) ─── */}
      {nextStep && (
        <div className="px-2 pt-1 pb-2">
          <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5">
            <div className="flex items-start justify-between gap-1 mb-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-primary-text">
                Next step
              </p>
              <button
                type="button"
                onClick={() => dismissNextStep(nextStep.key)}
                className="text-muted-foreground hover:text-foreground transition-colors -mt-0.5"
                aria-label={`Dismiss ${nextStep.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                navigate(nextStep.to);
                onClose?.();
              }}
              className="w-full text-left"
            >
              <p className="text-sm font-semibold text-foreground leading-snug">{nextStep.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{nextStep.description}</p>
            </button>
          </div>
        </div>
      )}

      {/* Skipped steps indicator */}
      {!nextStep && dismissed.length > 0 && (
        <div className="px-2 pt-1 pb-2">
          <button
            type="button"
            onClick={() => resetDismissedNextSteps()}
            className="w-full flex items-center justify-between rounded-lg border border-dashed border-border px-3 py-2 hover:bg-muted/50 transition-colors"
          >
            <span className="text-xs font-medium text-muted-foreground">Show next steps</span>
            <span className="text-xs font-semibold text-primary-text">→</span>
          </button>
        </div>
      )}

      {/* ── Divider ── */}
      <div className="mx-4 border-t border-border/40" />

      {/* ── Chat history (scrollable) ── */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="shrink-0 px-4 pt-3 pb-1.5 flex items-center justify-between">
          <p className="text-[11px] leading-4 tracking-[1.1px] uppercase text-muted-foreground">
            Your Conversations
          </p>
          <button
            type="button"
            className="h-6 w-6 rounded-md flex items-center justify-center text-primary-text hover:bg-primary/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={onNewChat}
            disabled={!!disableNewChat}
            aria-label="New chat"
          >
            <SquarePen className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-2">
          {chatHistory.length === 0 ? (
            <p className="text-xs text-muted-foreground px-3 py-3">No chats yet.</p>
          ) : (
            <div className="flex flex-col gap-0.5">
              {chatHistory.map(chat => (
                <button
                  key={chat.id}
                  type="button"
                  className={
                    "w-full h-7 rounded-lg px-3 flex items-center gap-2 text-left transition-colors " +
                    (activeId === chat.id && isChatPage
                      ? "bg-secondary/70 text-foreground"
                      : "text-foreground/70 hover:bg-secondary/40 hover:text-foreground")
                  }
                  onClick={() => {
                    trackChatThreadResumed({ threadId: chat.id });
                    onSelectChat?.(chat.id);
                  }}
                  aria-current={activeId === chat.id ? "page" : undefined}
                >
                  <CoachIcon className="h-4 w-4 opacity-60" />
                  <span className="text-xs truncate">{chat.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Footer: only for academy-branded accounts, whose header shows the academy logo ── */}
      {hasAcademyBrand && (
        <div className="shrink-0 border-t border-border px-4 py-2.5">
          <a
            href={SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[11px] font-medium text-subtle hover:text-forest"
          >
            Powered by
            <img src={BrandLogo} alt="golfrecruit.ai" className="h-4 w-auto" />
          </a>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
